/**
 * Token-bound monograph foundation primitives.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { colorWithAlpha } from "./MonographPanelColor.js";
import type {
  MonographBadgeProps,
  MonographChipRowProps,
  MonographKpiBoxProps,
  MonographPanelPrimitiveOptions,
  MonographPanelTokens,
  MonographVerdictBannerProps,
} from "./MonographPanelPrimitiveTypes.js";

interface FoundationFactoryInput {
  readonly tokens: MonographPanelTokens;
  readonly chipOptions: NonNullable<MonographPanelPrimitiveOptions["chip"]>;
  readonly kpiOptions: NonNullable<MonographPanelPrimitiveOptions["kpi"]>;
  readonly verdictOptions: NonNullable<MonographPanelPrimitiveOptions["verdict"]>;
}

export function createMonographPanelFoundations({
  tokens,
  chipOptions,
  kpiOptions,
  verdictOptions,
}: FoundationFactoryInput): {
  readonly Badge: (props: MonographBadgeProps) => React.JSX.Element;
  readonly ChipRow: <Id extends string = string>(
    props: MonographChipRowProps<Id>,
  ) => React.JSX.Element;
  readonly KpiBox: (props: MonographKpiBoxProps) => React.JSX.Element;
  readonly VerdictBanner: (props: MonographVerdictBannerProps) => React.JSX.Element;
} {
  function KpiBox({
    label,
    value,
    accent,
    color = accent ?? tokens.accent,
    subtitle,
    hint,
  }: MonographKpiBoxProps): React.JSX.Element {
    const detail = subtitle ?? hint;
    return (
      <div
        style={{
          background: kpiOptions.background ?? "rgba(7,6,13,0.6)",
          border: `1px solid ${tokens.border}`,
          borderRadius: 14,
          flex: `1 1 ${kpiOptions.minWidth ?? 90}px`,
          minWidth: kpiOptions.minWidth ?? 90,
          padding: kpiOptions.padding ?? "12px 14px",
        }}
      >
        <div
          style={{
            color,
            fontFamily: tokens.mono,
            fontSize: kpiOptions.valueFontSize ?? 22,
            fontWeight: 800,
            lineHeight: 1,
            textShadow: `0 0 16px ${colorWithAlpha(color, kpiOptions.textShadowOpacity ?? "50")}`,
          }}
        >
          {value}
        </div>
        <div
          style={{
            color: tokens.muted,
            fontSize: 11,
            lineHeight: 1.3,
            marginTop: kpiOptions.labelMarginTop ?? 4,
          }}
        >
          {label}
        </div>
        {detail ? (
          <div style={{ color: tokens.muted, fontSize: 10, marginTop: 2, opacity: 0.7 }}>
            {detail}
          </div>
        ) : null}
      </div>
    );
  }

  function Badge({
    children,
    label,
    color,
    tone = "neutral",
    title,
    className,
  }: MonographBadgeProps): React.JSX.Element {
    const content = children ?? label;
    const resolvedColor =
      color ??
      {
        accent: tokens.accent,
        bad: tokens.accentRed ?? tokens.accentForbidden ?? "#ef4444",
        danger: tokens.accentRed ?? tokens.accentForbidden ?? "#ef4444",
        good: tokens.accentGreen ?? tokens.accentSafe ?? "#22c55e",
        info: tokens.accentBlue ?? tokens.accent,
        neutral: tokens.muted,
        safe: tokens.accentGreen ?? tokens.accentSafe ?? "#22c55e",
        warn: tokens.accentAmber ?? "#f59e0b",
      }[tone];
    return (
      <span
        className={className}
        title={title}
        style={{
          background: colorWithAlpha(resolvedColor, "1f"),
          border: `1px solid ${colorWithAlpha(resolvedColor, "55")}`,
          borderRadius: 999,
          color: resolvedColor,
          display: "inline-flex",
          fontFamily: tokens.mono,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 0,
          lineHeight: 1.1,
          padding: "3px 8px",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {content}
      </span>
    );
  }

  function ChipRow<Id extends string = string>({
    items,
    activeId,
    onChange,
    ariaLabel,
  }: MonographChipRowProps<Id>): React.JSX.Element {
    return (
      <div
        role="tablist"
        aria-label={ariaLabel}
        style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
      >
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                onChange(item.id);
              }}
              style={{
                background: active
                  ? colorWithAlpha(item.color, "22")
                  : (chipOptions.inactiveBackground ?? "rgba(7,6,13,0.4)"),
                border: active ? `1px solid ${item.color}` : `1px solid ${tokens.border}`,
                borderRadius: 999,
                boxShadow: active ? `0 0 14px ${colorWithAlpha(item.color, "33")}` : "none",
                color: active ? item.color : tokens.text,
                cursor: "pointer",
                fontSize: chipOptions.fontSize ?? 12.5,
                fontWeight: 600,
                padding: "8px 14px",
                transition: "all 120ms ease",
              }}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    );
  }

  function VerdictBanner({
    tone,
    title,
    body,
    outcome,
    tag,
    explanation,
  }: MonographVerdictBannerProps): React.JSX.Element {
    const resolvedTone =
      tone ?? (outcome === "ADMITTED" ? "safe" : outcome === "BOUNDARY" ? "neutral" : "unsafe");
    const resolvedTitle = title ?? `${outcome ?? "Verdict"}${tag === undefined ? "" : ` · ${tag}`}`;
    const resolvedBody = body ?? explanation;
    const color =
      resolvedTone === "safe"
        ? (verdictOptions.safeColor ?? tokens.accentGreen ?? tokens.accentSafe ?? "#22c55e")
        : resolvedTone === "unsafe"
          ? (verdictOptions.unsafeColor ?? tokens.accentRed ?? tokens.accentForbidden ?? "#ef4444")
          : (verdictOptions.neutralColor ?? tokens.accentBlue ?? tokens.accent);
    const symbol = resolvedTone === "safe" ? "✓" : resolvedTone === "unsafe" ? "✗" : "•";
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          alignItems: "flex-start",
          background: colorWithAlpha(color, "10"),
          border: `1px solid ${colorWithAlpha(color, "55")}`,
          borderLeft: `4px solid ${color}`,
          borderRadius: 12,
          display: "flex",
          gap: 14,
          padding: "14px 16px",
        }}
      >
        <span
          aria-hidden
          style={{
            alignItems: "center",
            background: colorWithAlpha(color, "22"),
            borderRadius: 8,
            color,
            display: "inline-flex",
            flexShrink: 0,
            fontSize: 16,
            fontWeight: 800,
            height: 28,
            justifyContent: "center",
            textShadow: `0 0 12px ${color}`,
            width: 28,
          }}
        >
          {symbol}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: tokens.bright, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            {resolvedTitle}
          </div>
          <div style={{ color: tokens.text, fontSize: 13, lineHeight: 1.6 }}>{resolvedBody}</div>
        </div>
      </div>
    );
  }

  return { Badge, ChipRow, KpiBox, VerdictBanner };
}
